<template>
  <div class="goods-group-setting">
    <div class="setting-label">商品管理</div>
    <el-button class="add-button" type="text" @click="pickerVisible = true">＋ 添加商品分组</el-button>
    <draggable
      v-if="selectedGroups.length"
      v-model="selectedGroups"
      animation="200"
      draggable=".selected-group"
      handle=".selected-group"
      filter=".remove-group,.group-toggle"
      :prevent-on-filter="false"
      :force-fallback="true"
      ghost-class="group-sort-ghost"
      class="selected-groups"
    >
      <div
        v-for="group in selectedGroups"
        :key="group.group_id"
        class="selected-group"
        :class="{ active: String(group.group_id) === String(editingGroupId) }"
        @click="selectGroup(group)"
      >
        <i class="el-icon-rank drag-handle"></i>
        <span class="selected-name">{{ displayGroupName(group) }}</span>
        <i
          class="el-icon-arrow-down group-toggle"
          :class="{ expanded: String(group.group_id) === String(editingGroupId) }"
        ></i>
        <i class="el-icon-close remove-group" @click.stop="removeGroup(group.group_id)"></i>
      </div>
    </draggable>
    <p v-else class="empty-tip">暂未选择商品分组</p>
    <p class="setting-tip">按住分组行上下拖动，左侧 Tab 实时按新顺序排列，保存后生效；组内商品排序不变。</p>

    <div v-if="editingGroup" class="group-config">
      <div class="group-config-title">分组设置</div>
      <div class="group-config-row">
        <span class="group-config-label">分组别名：</span>
        <el-input
          :value="editingGroup.display_name"
          size="small"
          maxlength="20"
          placeholder="请输入装修中展示的分组名称"
          @input="updateDisplayName"
        />
      </div>
    </div>

    <el-dialog title="选择商品分组" :visible.sync="pickerVisible" width="620px" append-to-body @opened="filterGroups">
      <el-input
        v-model="groupKeyword"
        class="group-search"
        placeholder="请输入商品分组名称"
        prefix-icon="el-icon-search"
        clearable
        size="small"
      />
      <div class="picker-count">已选择 {{ selectedCount }} 个</div>
      <div v-loading="loading" class="group-tree-wrap">
        <el-tree
          ref="groupTree"
          :data="options"
          :props="treeProps"
          node-key="group_id"
          :filter-node-method="filterGroupNode"
          show-checkbox
          default-expand-all
          empty-text="暂无匹配的商品分组"
          @check="handleCheck"
        >
          <span slot-scope="{ data }" class="group-node">
            <span class="group-name">{{ data.group_name }}</span>
            <span class="goods-count">{{ data.goods_count || 0 }} 件商品</span>
          </span>
        </el-tree>
      </div>
      <span slot="footer">
        <el-button @click="pickerVisible = false">取消</el-button>
        <el-button type="primary" @click="pickerVisible = false">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import { getVisibleGoodsGroups, getVisibleGoodsGroup } from '@/api/goods-group'

const normalizeListStyle = style => ({
  BIG: 'single',
  SMALL: 'row-two',
  MIXED: 'hybrid',
  DETAIL: 'column'
}[String(style || '').toUpperCase()] || style || 'single')

export default {
  name: 'GoodsGroupSettingSelector',
  components: { Draggable },
  data() {
    return {
      loading: false,
      pickerVisible: false,
      groupKeyword: '',
      options: [],
      selectedCount: 0,
      editingGroupId: '',
      refreshSequence: 0,
      treeProps: { label: 'group_name', children: 'children' }
    }
  },
  computed: {
    selectedGroups: {
      get() { return Array.isArray(this.$$FormData.groups) ? this.$$FormData.groups : [] },
      // 拖拽以新数组统一写回分组及ID顺序，预览、保存与买家刷新使用同一顺序。
      set(groups) { this.applyGroups(groups) }
    },
    editingGroup() {
      return this.selectedGroups.find(group => String(group.group_id) === String(this.editingGroupId)) || null
    }
  },
  watch: {
    groupKeyword() { this.filterGroups() }
  },
  mounted() { this.loadGroups() },
  methods: {
    /** 只过滤树节点显示，不重建分组数据或修改已勾选项。 */
    filterGroups() {
      if (this.$refs.groupTree) this.$refs.groupTree.filter(this.groupKeyword)
    },
    /** 名称模糊搜索忽略首尾空格和大小写，命中一级分组时同时显示其子分组。 */
    filterGroupNode(value, data, node) {
      const keyword = String(value || '').trim().toLowerCase()
      if (!keyword) return true
      if (String(data.group_name || '').toLowerCase().includes(keyword)) return true
      let parent = node && node.parent
      while (parent) {
        if (String((parent.data && parent.data.group_name) || '').toLowerCase().includes(keyword)) return true
        parent = parent.parent
      }
      return false
    },
    async loadGroups() {
      this.loading = true
      try {
        const groups = await getVisibleGoodsGroups()
        this.options = (groups || []).map(parent => {
          const option = Object.assign({}, parent)
          const children = (parent.children || []).map(child => {
            const childOption = Object.assign({}, child)
            delete childOption.children
            return childOption
          })
          if (children.length) option.children = children
          else delete option.children
          return option
        })
        const ids = Array.isArray(this.$$FormData.group_ids) && this.$$FormData.group_ids.length
          ? this.$$FormData.group_ids
          : (this.$$FormData.group_id ? [this.$$FormData.group_id] : [])
        this.$set(this.$$FormData, 'group_ids', ids)
        this.selectedCount = ids.length
        if (this.selectedGroups.length) {
          this.selectedGroups.forEach(group => {
            if (!Object.prototype.hasOwnProperty.call(group, 'display_name')) {
              this.$set(group, 'display_name', group.group_name || '')
            }
          })
          this.syncLegacyGroupName()
        }
        this.$nextTick(() => {
          if (this.$refs.groupTree) this.$refs.groupTree.setCheckedKeys(ids)
          this.filterGroups()
        })
        if (ids.length && !this.selectedGroups.length) await this.refreshGroups(ids)
      } finally {
        this.loading = false
      }
    },
    handleCheck() {
      const ids = this.$refs.groupTree ? this.$refs.groupTree.getCheckedKeys() : []
      this.refreshGroups(ids)
    },
    async refreshGroups(ids) {
      this.selectedCount = ids.length
      const sequence = ++this.refreshSequence
      if (!ids.length) {
        this.applyGroups([])
        return
      }
      const groups = await Promise.all(ids.map(id => getVisibleGoodsGroup(id)))
      if (sequence !== this.refreshSequence) return
      const oldGroups = new Map(this.selectedGroups.map(group => [String(group.group_id), group]))
      const oldOrder = new Map(this.selectedGroups.map((group, index) => [String(group.group_id), index]))
      groups.sort((a, b) => {
        const ai = oldOrder.has(String(a.group_id)) ? oldOrder.get(String(a.group_id)) : Number.MAX_SAFE_INTEGER
        const bi = oldOrder.has(String(b.group_id)) ? oldOrder.get(String(b.group_id)) : Number.MAX_SAFE_INTEGER
        return ai - bi
      })
      this.applyGroups(groups.map(group => {
        const oldGroup = oldGroups.get(String(group.group_id))
        return {
          group_id: group.group_id,
          group_name: group.group_name,
          display_name: oldGroup && Object.prototype.hasOwnProperty.call(oldGroup, 'display_name')
            ? oldGroup.display_name
            : group.group_name,
          list_style: normalizeListStyle(group.list_style),
          goodsList: (group.goods || []).map(item => Object.assign({}, item, {
            goods_image: item.goods_image || item.thumbnail || item.small,
            goods_price: item.goods_price == null ? item.price : item.goods_price
          }))
        }
      }))
    },
    applyGroups(groups) {
      const ids = groups.map(group => group.group_id)
      this.$set(this.$$FormData, 'groups', groups)
      this.$set(this.$$FormData, 'group_ids', ids)
      this.$$FormData.group_id = ids.length ? ids[0] : ''
      this.syncLegacyGroupName()
      this.$$FormData.goodsList = groups.length ? groups[0].goodsList : []
      if (!groups.some(group => String(group.group_id) === String(this.editingGroupId))) {
        this.editingGroupId = ''
      }
    },
    displayGroupName(group) {
      return String((group && group.display_name) || '').trim() || (group && group.group_name) || ''
    },
    syncLegacyGroupName() {
      this.$$FormData.group_name = this.selectedGroups.map(group => this.displayGroupName(group)).join('、')
    },
    selectGroup(group) {
      const isEditing = String(group.group_id) === String(this.editingGroupId)
      this.editingGroupId = isEditing ? '' : group.group_id
      if (!Object.prototype.hasOwnProperty.call(group, 'display_name')) {
        this.$set(group, 'display_name', group.group_name || '')
      }
    },
    updateDisplayName(value) {
      if (!this.editingGroup) return
      this.$set(this.editingGroup, 'display_name', value)
      this.syncLegacyGroupName()
    },
    removeGroup(groupId) {
      const groups = this.selectedGroups.filter(group => String(group.group_id) !== String(groupId))
      this.applyGroups(groups)
      this.selectedCount = groups.length
      this.$nextTick(() => {
        if (this.$refs.groupTree) this.$refs.groupTree.setCheckedKeys(groups.map(group => group.group_id))
      })
    }
  }
}
</script>

<style scoped>
.goods-group-setting { padding: 16px; background: #fff; }
.group-search { margin-bottom: 12px; }
.setting-label { margin-bottom: 8px; color: #606266; }
.add-button { width: 100%; padding: 12px; border: 1px dashed #409eff; }
.selected-groups { margin-top: 12px; border: 1px solid #ebeef5; }
.selected-group { display: flex; align-items: center; height: 42px; padding: 0 12px; border: 1px solid transparent; border-bottom-color: #ebeef5; cursor: grab; user-select: none; }
.selected-group:active { cursor: grabbing; }
.group-sort-ghost { opacity: .4; background: #eaf3ff; }
.selected-group.active { border: 1px solid #409eff; background: #f4f8ff; }
.selected-group:last-child { border-bottom: 0; }
.selected-group.active:last-child { border-bottom: 1px solid #409eff; }
.drag-handle { margin-right: 10px; color: #c0c4cc; cursor: move; }
.selected-name { flex: 1; color: #606266; }
.group-toggle { width: 18px; height: 18px; margin-right: 10px; border: 1px solid #c0c4cc; border-radius: 50%; color: #c0c4cc; font-size: 12px; line-height: 17px; text-align: center; transition: transform .2s; }
.group-toggle.expanded { transform: rotate(180deg); }
.remove-group { color: #c0c4cc; cursor: pointer; }
.remove-group:hover { color: #f56c6c; }
.empty-tip { margin: 12px 0 0; color: #909399; text-align: center; }
.setting-tip { margin: 10px 0 0; color: #909399; font-size: 12px; line-height: 1.6; }
.group-config { margin-top: 18px; padding-top: 16px; border-top: 1px solid #ebeef5; }
.group-config-title { margin-bottom: 14px; color: #303133; font-weight: 600; }
.group-config-row { display: flex; align-items: center; }
.group-config-label { width: 78px; flex: none; color: #909399; }
.picker-count { padding: 8px 10px; color: #606266; background: #f5f7fa; border: 1px solid #ebeef5; border-bottom: 0; }
.group-tree-wrap { min-height: 160px; max-height: 420px; overflow-y: auto; border: 1px solid #ebeef5; }
.group-tree-wrap .el-tree { padding: 6px 0; }
.group-node { display: flex; flex: 1; align-items: center; justify-content: space-between; padding-right: 18px; }
.group-name { color: #409eff; }
.goods-count { color: #909399; font-size: 12px; }
</style>
